import { useState } from 'react';

// Mirror of the server-side CoverageStatus type — kept in sync manually
type CoverageStatus = 'operational' | 'partial' | 'no_state_requirement' | 'unavailable';

interface StateCoverage {
  code: string;
  name: string;
  status: CoverageStatus;
  url: string | null;
  notes: string;
}

// Static data mirrored from stateBoardScraper.ts STATE_COVERAGE_MAP
// Keep in sync when scraper coverage changes.
const COVERAGE: StateCoverage[] = [
  { code: 'AL', name: 'Alabama',              status: 'partial',              url: 'https://elicense.alabama.gov',             notes: 'HTML lookup; may require session' },
  { code: 'AK', name: 'Alaska',               status: 'no_state_requirement', url: null,                                        notes: 'No statewide contractor license; municipal only' },
  { code: 'AZ', name: 'Arizona',              status: 'operational',          url: 'https://roc.az.gov',                       notes: 'JSON API via ROC' },
  { code: 'AR', name: 'Arkansas',             status: 'partial',              url: 'https://contractors.arkansas.gov',         notes: 'HTML lookup' },
  { code: 'CA', name: 'California',           status: 'operational',          url: 'https://www.cslb.ca.gov',                  notes: 'CSLB JSON API' },
  { code: 'CO', name: 'Colorado',             status: 'partial',              url: 'https://apps2.colorado.gov/dora/licensing', notes: 'HTML lookup via DORA' },
  { code: 'CT', name: 'Connecticut',          status: 'partial',              url: 'https://elicense.ct.gov',                  notes: 'JS-heavy portal; HTML fallback' },
  { code: 'DE', name: 'Delaware',             status: 'partial',              url: 'https://dpr.delaware.gov',                 notes: 'HTML lookup' },
  { code: 'FL', name: 'Florida',              status: 'operational',          url: 'https://www.myfloridalicense.com',          notes: 'DBPR JSON API' },
  { code: 'GA', name: 'Georgia',              status: 'operational',          url: 'https://sos.ga.gov',                       notes: 'URL-based HTML lookup' },
  { code: 'HI', name: 'Hawaii',               status: 'partial',              url: 'https://pvl.ehawaii.gov',                  notes: 'HTML lookup via PVL' },
  { code: 'ID', name: 'Idaho',                status: 'partial',              url: 'https://dbs.idaho.gov',                    notes: 'HTML lookup' },
  { code: 'IL', name: 'Illinois',             status: 'partial',              url: 'https://online-dfpr.micropact.com',         notes: 'HTML lookup via IDFPR' },
  { code: 'IN', name: 'Indiana',              status: 'partial',              url: 'https://pla.in.gov',                       notes: 'HTML lookup via PLA' },
  { code: 'IA', name: 'Iowa',                 status: 'partial',              url: 'https://plc.iowa.gov',                     notes: 'HTML lookup' },
  { code: 'KS', name: 'Kansas',               status: 'partial',              url: 'https://www.ksbtp.ks.gov',                 notes: 'HTML lookup' },
  { code: 'KY', name: 'Kentucky',             status: 'partial',              url: 'https://pla.ky.gov',                       notes: 'HTML lookup' },
  { code: 'LA', name: 'Louisiana',            status: 'partial',              url: 'https://lslbc.louisiana.gov',              notes: 'POST form submission' },
  { code: 'ME', name: 'Maine',                status: 'partial',              url: 'https://www.maine.gov/pfr',                notes: 'HTML lookup' },
  { code: 'MD', name: 'Maryland',             status: 'operational',          url: 'https://www.dllr.state.md.us',             notes: 'URL-based CGI lookup' },
  { code: 'MA', name: 'Massachusetts',        status: 'partial',              url: 'https://elicensing.state.ma.us',           notes: 'POST form submission' },
  { code: 'MI', name: 'Michigan',             status: 'partial',              url: 'https://aca-prod.accela.com/MICHIGAN',      notes: 'Accela portal; HTML' },
  { code: 'MN', name: 'Minnesota',            status: 'partial',              url: 'https://secure.dli.mn.gov',                notes: 'HTML lookup via DLI' },
  { code: 'MS', name: 'Mississippi',          status: 'partial',              url: 'https://www.msboc.us',                     notes: 'HTML lookup' },
  { code: 'MO', name: 'Missouri',             status: 'partial',              url: 'https://pr.mo.gov',                        notes: 'HTML lookup via DPR' },
  { code: 'MT', name: 'Montana',              status: 'partial',              url: 'https://bsd.dli.mt.gov',                   notes: 'HTML lookup' },
  { code: 'NE', name: 'Nebraska',             status: 'partial',              url: 'https://nlicense.nebraska.gov',             notes: 'Trade licenses via DHHS; general contractors unlicensed at state level' },
  { code: 'NV', name: 'Nevada',               status: 'operational',          url: 'https://www.nscb.nv.gov',                  notes: 'JSON API + HTML fallback via NSCB' },
  { code: 'NH', name: 'New Hampshire',        status: 'no_state_requirement', url: null,                                        notes: 'General contractors unlicensed at state level' },
  { code: 'NJ', name: 'New Jersey',           status: 'partial',              url: 'https://www.njconsumeraffairs.gov',         notes: 'HIC registration HTML lookup' },
  { code: 'NM', name: 'New Mexico',           status: 'partial',              url: 'https://www.rld.nm.gov',                   notes: 'HTML lookup via CID' },
  { code: 'NY', name: 'New York',             status: 'partial',              url: 'https://a810-bisweb.nyc.gov',              notes: 'NYC-only; state licensing is local' },
  { code: 'NC', name: 'North Carolina',       status: 'operational',          url: 'https://nclbgc.org',                       notes: 'URL-based JSON via NCLBGC' },
  { code: 'ND', name: 'North Dakota',         status: 'no_state_requirement', url: null,                                        notes: 'No statewide contractor license' },
  { code: 'OH', name: 'Ohio',                 status: 'partial',              url: 'https://license.ohio.gov',                 notes: 'HTML lookup via DIC' },
  { code: 'OK', name: 'Oklahoma',             status: 'partial',              url: 'https://cib.ok.gov',                       notes: 'HTML lookup via CIB' },
  { code: 'OR', name: 'Oregon',               status: 'operational',          url: 'https://ccb.oregon.gov',                   notes: 'JSON API via CCB' },
  { code: 'PA', name: 'Pennsylvania',         status: 'partial',              url: 'https://www.pals.pa.gov',                  notes: 'PALS REST API (HIC registry)' },
  { code: 'RI', name: 'Rhode Island',         status: 'partial',              url: 'https://www.crb.ri.gov',                   notes: 'HTML lookup via CRLB' },
  { code: 'SC', name: 'South Carolina',       status: 'operational',          url: 'https://www.llronline.com',                notes: 'URL-based HTML via LLR' },
  { code: 'TN', name: 'Tennessee',            status: 'partial',              url: 'https://verify.tn.gov',                    notes: 'HTML lookup' },
  { code: 'TX', name: 'Texas',                status: 'operational',          url: 'https://www.license.state.tx.us',          notes: 'TDLR JSON API' },
  { code: 'UT', name: 'Utah',                 status: 'partial',              url: 'https://dopl.utah.gov',                    notes: 'HTML lookup via DOPL' },
  { code: 'VT', name: 'Vermont',              status: 'partial',              url: 'https://sec.vermont.gov/opr',              notes: 'HTML lookup' },
  { code: 'VA', name: 'Virginia',             status: 'operational',          url: 'https://www.dpor.virginia.gov',            notes: 'DPOR JSON API + HTML fallback' },
  { code: 'WA', name: 'Washington',           status: 'operational',          url: 'https://secure.lni.wa.gov',                notes: 'LNI JSON API — most reliable in set' },
  { code: 'WV', name: 'West Virginia',        status: 'partial',              url: 'https://labor.wv.gov',                     notes: 'HTML lookup' },
  { code: 'WI', name: 'Wisconsin',            status: 'partial',              url: 'https://licensesearch.wi.gov',             notes: 'URL-based lookup via DSPS' },
  { code: 'WY', name: 'Wyoming',              status: 'partial',              url: 'https://wbc.wyo.gov',                      notes: 'HTML lookup via WBC' },
  { code: 'DC', name: 'District of Columbia', status: 'partial',              url: 'https://eservices.dcra.dc.gov',            notes: 'JSON API attempted; HTML fallback' },
];

const STATUS_CONFIG: Record<CoverageStatus, { label: string; color: string; bg: string; dot: string }> = {
  operational:          { label: 'Operational',          color: '#166534', bg: '#dcfce7', dot: '#16a34a' },
  partial:              { label: 'Partial / HTML',        color: '#854d0e', bg: '#fef9c3', dot: '#ca8a04' },
  no_state_requirement: { label: 'No state requirement',  color: '#1e3a5f', bg: '#dbeafe', dot: '#3b82f6' },
  unavailable:          { label: 'Unavailable',           color: '#7f1d1d', bg: '#fee2e2', dot: '#ef4444' },
};

const COUNTS = {
  operational:          COVERAGE.filter(s => s.status === 'operational').length,
  partial:              COVERAGE.filter(s => s.status === 'partial').length,
  no_state_requirement: COVERAGE.filter(s => s.status === 'no_state_requirement').length,
  unavailable:          COVERAGE.filter(s => s.status === 'unavailable').length,
};

export function LicenseCoverageMap() {
  const [filter, setFilter] = useState<CoverageStatus | 'all'>('all');
  const [selected, setSelected] = useState<StateCoverage | null>(null);

  const visible = filter === 'all' ? COVERAGE : COVERAGE.filter(s => s.status === filter);

  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', maxWidth: 1200 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>License Scraper Coverage</h1>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        {COVERAGE.length} jurisdictions tracked &mdash; {COUNTS.operational} fully operational, {COUNTS.partial} partial (HTML), {COUNTS.no_state_requirement} no state requirement
      </p>

      {/* Legend / filter pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
        <FilterPill label="All" active={filter === 'all'} count={COVERAGE.length} onClick={() => setFilter('all')} dot="#6b7280" />
        {(Object.entries(STATUS_CONFIG) as [CoverageStatus, typeof STATUS_CONFIG['operational']][]).map(([key, cfg]) => (
          <FilterPill
            key={key}
            label={cfg.label}
            active={filter === key}
            count={COUNTS[key]}
            onClick={() => setFilter(key === filter ? 'all' : key)}
            dot={cfg.dot}
          />
        ))}
      </div>

      {/* State grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
        {visible.map(state => {
          const cfg = STATUS_CONFIG[state.status];
          return (
            <button
              key={state.code}
              onClick={() => setSelected(selected?.code === state.code ? null : state)}
              style={{
                background: selected?.code === state.code ? cfg.dot : cfg.bg,
                color: selected?.code === state.code ? '#fff' : cfg.color,
                border: `1.5px solid ${cfg.dot}`,
                borderRadius: 8,
                padding: '10px 14px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: selected?.code === state.code ? '#fff' : cfg.dot, display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontWeight: 700, fontSize: 13 }}>{state.code}</span>
              </div>
              <div style={{ fontSize: 11, marginTop: 2, opacity: 0.85 }}>{state.name}</div>
            </button>
          );
        })}
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{
          marginTop: 24,
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{selected.name} ({selected.code})</h2>
              <span style={{
                display: 'inline-block',
                marginTop: 6,
                padding: '2px 10px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                background: STATUS_CONFIG[selected.status].bg,
                color: STATUS_CONFIG[selected.status].color,
                border: `1px solid ${STATUS_CONFIG[selected.status].dot}`,
              }}>
                {STATUS_CONFIG[selected.status].label}
              </span>
            </div>
            <button
              onClick={() => setSelected(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', padding: 4 }}
            >
              &times;
            </button>
          </div>
          <p style={{ marginTop: 12, color: '#374151', fontSize: 14 }}>{selected.notes}</p>
          {selected.url && (
            <p style={{ marginTop: 8, fontSize: 13 }}>
              Board URL:{' '}
              <a href={selected.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
                {selected.url}
              </a>
            </p>
          )}
          {!selected.url && (
            <p style={{ marginTop: 8, color: '#6b7280', fontSize: 13, fontStyle: 'italic' }}>
              No statewide board URL — licensing is local or trade-specific.
            </p>
          )}
        </div>
      )}

      {/* Summary table */}
      <div style={{ marginTop: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Full Coverage Table</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                {['State', 'Name', 'Status', 'Board URL', 'Notes'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((s, i) => {
                const cfg = STATUS_CONFIG[s.status];
                return (
                  <tr key={s.code} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '7px 12px', fontWeight: 700 }}>{s.code}</td>
                    <td style={{ padding: '7px 12px' }}>{s.name}</td>
                    <td style={{ padding: '7px 12px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 12, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 600 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot }} />
                        {cfg.label}
                      </span>
                    </td>
                    <td style={{ padding: '7px 12px' }}>
                      {s.url
                        ? <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>{s.url.replace('https://', '')}</a>
                        : <span style={{ color: '#9ca3af' }}>—</span>
                      }
                    </td>
                    <td style={{ padding: '7px 12px', color: '#6b7280' }}>{s.notes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FilterPill({ label, active, count, onClick, dot }: { label: string; active: boolean; count: number; onClick: () => void; dot: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        borderRadius: 20,
        border: `1.5px solid ${active ? dot : '#e5e7eb'}`,
        background: active ? dot : '#fff',
        color: active ? '#fff' : '#374151',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#fff' : dot }} />
      {label}
      <span style={{
        background: active ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
        borderRadius: 10,
        padding: '0 6px',
        fontSize: 11,
        fontWeight: 700,
      }}>{count}</span>
    </button>
  );
}
